import type { SpreadTextFit } from '@kitup/book-schema';
import type { JSX } from 'react';
import { useI18n } from '../i18n/context.js';

/**
 * Under a spread's text: the comfortable length the AI writes to, as guidance, and the
 * hard limit — the renderer's own measure at its smallest font — as the refusal.
 */
export function TextFitHint({
  fit,
  length,
  target,
  baseSizePt,
}: {
  readonly fit: SpreadTextFit;
  /** The book's base font size: "smaller type" is said only when the fit picks below it. */
  readonly baseSizePt: number;
  readonly length: number;
  /** The comfortable length for this layout: `textBudgetFor(...).perSpread`. */
  readonly target: number;
}): JSX.Element {
  const { t } = useI18n();
  if (!fit.fits) {
    // A warning reads as a sentence; right-aligned over three lines on a phone it did not.
    return (
      <p className="mt-1 text-xs font-semibold text-red-700">
        {t.story.ai.textFit.overflow(fit.overflowCharacters)}
      </p>
    );
  }
  // The renderer's own fit decides whether the type shrinks; length alone does not.
  if (fit.fontSizePt < baseSizePt) {
    return <p className="mt-1 text-xs text-amber-800">{t.story.ai.textFit.smaller(target)}</p>;
  }
  return length > target ? (
    <p className="mt-1 text-right text-xs text-[var(--kitup-color-muted)]">
      {t.story.ai.textFit.longer(target)}
    </p>
  ) : (
    <p className="mt-1 text-right text-xs text-[var(--kitup-color-muted)]">
      {t.story.ai.textFit.left(target - length, target)}
    </p>
  );
}
